import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import type { Alert, CohortRetention, DailyAggregate } from '@etiquetachef/types';
import { serverDate } from '@etiquetachef/ui-shared/online';

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#0f172a',
  },
  h1: { fontSize: 20, marginBottom: 16, fontFamily: 'Helvetica-Bold' },
  h2: { fontSize: 14, marginBottom: 8, fontFamily: 'Helvetica-Bold' },
  kpiRow: { flexDirection: 'row', gap: 12, marginBottom: 12 },
  kpi: { flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 4 },
  kpiLabel: { fontSize: 8, color: '#64748b', marginBottom: 4 },
  kpiValue: { fontSize: 14, fontFamily: 'Helvetica-Bold' },
  table: { display: 'flex', flexDirection: 'column', marginBottom: 16 },
  row: { flexDirection: 'row', borderBottom: '1px solid #e2e8f0', paddingVertical: 4 },
  rowHead: { flexDirection: 'row', borderBottom: '2px solid #94a3b8', paddingVertical: 4 },
  cell: { flex: 1, fontSize: 9 },
  cellHead: { flex: 1, fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#475569' },
  small: { fontSize: 8, color: '#475569' },
});

/** Centavos de USD → `$1234.56`. */
function fmtUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Fração (0–1) → `12.3%`. */
function fmtPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

/** `yyyymmdd` → `DD/MM`. */
function fmtDate(yyyymmdd: string): string {
  return `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}`;
}

export type MonthlyReportPdfProps = {
  yyyymm: string;
  mrrEndUsdCents: number;
  netNewMrrUsdCents: number;
  newTenants: number;
  churnedTenants: number;
  churnRatePct: number;
  series: readonly DailyAggregate[];
  cohort: CohortRetention | null;
  highAlerts: readonly Alert[];
};

/** Documento react-pdf do relatório mensal (KPIs, série diária, coorte, alertas HIGH); valores em centavos de USD. */
export function MonthlyReportPdf({
  yyyymm,
  mrrEndUsdCents,
  netNewMrrUsdCents,
  newTenants,
  churnedTenants,
  churnRatePct,
  series,
  cohort,
  highAlerts,
}: MonthlyReportPdfProps): React.JSX.Element {
  const monthLabel = `${yyyymm.slice(4, 6)}/${yyyymm.slice(0, 4)}`;
  return (
    <Document>
      {/* Página 1: capa */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.h1}>EtiquetaChef — Relatório mensal</Text>
        <Text style={styles.h2}>{monthLabel}</Text>
        <View style={styles.kpiRow}>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>MRR fim (USD eq.)</Text>
            <Text style={styles.kpiValue}>{fmtUsd(mrrEndUsdCents)}</Text>
          </View>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>Net New MRR</Text>
            <Text style={styles.kpiValue}>{fmtUsd(netNewMrrUsdCents)}</Text>
          </View>
        </View>
        <View style={styles.kpiRow}>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>Vendas</Text>
            <Text style={styles.kpiValue}>{newTenants}</Text>
          </View>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>Cancelamentos</Text>
            <Text style={styles.kpiValue}>{churnedTenants}</Text>
          </View>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>Churn rate</Text>
            <Text style={styles.kpiValue}>{fmtPct(churnRatePct)}</Text>
          </View>
        </View>
        <Text style={styles.small}>
          Gerado em {serverDate().toISOString().slice(0, 10)}.
        </Text>
      </Page>

      {/* Página 2: série diária */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.h2}>Série diária ({series.length} dias)</Text>
        <View style={styles.table}>
          <View style={styles.rowHead}>
            <Text style={styles.cellHead}>Dia</Text>
            <Text style={styles.cellHead}>MRR (USD)</Text>
            <Text style={styles.cellHead}>Vendas</Text>
            <Text style={styles.cellHead}>Etiquetas</Text>
            <Text style={styles.cellHead}>Net New (USD)</Text>
          </View>
          {series.map((d) => (
            <View key={d.yyyymmdd} style={styles.row}>
              <Text style={styles.cell}>{fmtDate(d.yyyymmdd)}</Text>
              <Text style={styles.cell}>{fmtUsd(d.mrr.usdEquivalentCents)}</Text>
              <Text style={styles.cell}>{d.tenants.createdToday}</Text>
              <Text style={styles.cell}>{d.labels.printedToday ?? '—'}</Text>
              <Text style={styles.cell}>{fmtUsd(d.netNewMrr.netUsdCents)}</Text>
            </View>
          ))}
        </View>
        <Text style={styles.small}>
          Charts visuais (PNG) ficam para iteração futura — esta versão usa
          tabela tabular que abre em qualquer reader.
        </Text>
      </Page>

      {/* Página 3: cohort */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.h2}>Cohort retention — safra do mês</Text>
        {cohort ? (
          <>
            <Text style={styles.small}>
              Tamanho do cohort: {cohort.cohortSize}
            </Text>
            <View style={[styles.table, { marginTop: 8 }]}>
              <View style={styles.rowHead}>
                <Text style={styles.cellHead}>Janela</Text>
                <Text style={styles.cellHead}>Retenção</Text>
                <Text style={styles.cellHead}>Churn cumul.</Text>
              </View>
              {(['d1', 'd7', 'd14', 'd30', 'd60', 'd90'] as const).map((w) => (
                <View key={w} style={styles.row}>
                  <Text style={styles.cell}>D{w.slice(1)}</Text>
                  <Text style={styles.cell}>{fmtPct(cohort.retention[w])}</Text>
                  <Text style={styles.cell}>
                    {fmtPct(cohort.cumulativeChurn[w])}
                  </Text>
                </View>
              ))}
            </View>
          </>
        ) : (
          <Text style={styles.small}>Cohort do mês ainda não foi gerado.</Text>
        )}
      </Page>

      {/* Página 4: top dias */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.h2}>Top 10 dias por MRR</Text>
        <View style={styles.table}>
          <View style={styles.rowHead}>
            <Text style={styles.cellHead}>Dia</Text>
            <Text style={styles.cellHead}>MRR (USD)</Text>
            <Text style={styles.cellHead}>Vendas</Text>
            <Text style={styles.cellHead}>Net New (USD)</Text>
          </View>
          {[...series]
            .sort((a, b) => b.mrr.usdEquivalentCents - a.mrr.usdEquivalentCents)
            .slice(0, 10)
            .map((d) => (
              <View key={d.yyyymmdd} style={styles.row}>
                <Text style={styles.cell}>{fmtDate(d.yyyymmdd)}</Text>
                <Text style={styles.cell}>{fmtUsd(d.mrr.usdEquivalentCents)}</Text>
                <Text style={styles.cell}>{d.tenants.createdToday}</Text>
                <Text style={styles.cell}>{fmtUsd(d.netNewMrr.netUsdCents)}</Text>
              </View>
            ))}
        </View>
      </Page>

      {/* Página 5: alertas críticos */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.h2}>
          Alertas críticos ({highAlerts.length})
        </Text>
        {highAlerts.length === 0 ? (
          <Text style={styles.small}>Nenhum alerta HIGH neste mês.</Text>
        ) : (
          <View style={styles.table}>
            <View style={styles.rowHead}>
              <Text style={styles.cellHead}>Tipo</Text>
              <Text style={styles.cellHead}>Período</Text>
              <Text style={styles.cellHead}>Magnitude</Text>
              <Text style={styles.cellHead}>Detalhe</Text>
            </View>
            {highAlerts.map((a) => (
              <View key={a.alertId} style={styles.row}>
                <Text style={styles.cell}>{a.kind}</Text>
                <Text style={styles.cell}>
                  {a.periodAffected.fromYyyymmdd}
                  {a.periodAffected.fromYyyymmdd !== a.periodAffected.toYyyymmdd
                    ? `–${a.periodAffected.toYyyymmdd}`
                    : ''}
                </Text>
                <Text style={styles.cell}>{a.magnitude.toFixed(3)}</Text>
                <Text style={[styles.cell, { flex: 2 }]}>{a.detail}</Text>
              </View>
            ))}
          </View>
        )}
      </Page>
    </Document>
  );
}
